import * as React from 'react';
import { CircleAlert, TriangleAlert } from 'lucide-react';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { cn } from '../../lib/utils';

// Promise-based replacement for window.confirm, rendered as a shadcn AlertDialog:
//
//   if (!(await confirm({ title: 'Delete entry?', tone: 'danger', confirmText: 'Delete' }))) return;
//
// Resolves true for the confirm button, false for Cancel / Esc / outside click —
// the same two outcomes window.confirm had. <ConfirmHost /> is mounted once at
// the app root; calls queue if several arrive at once.
let enqueue = null;

export function confirm(options) {
    const opts = typeof options === 'string' ? { description: options } : options || {};
    if (!enqueue) {
        // Host not mounted (shouldn't happen) — fall back rather than silently proceed.
        return Promise.resolve(window.confirm([opts.title, opts.description].filter(Boolean).join('\n\n')));
    }
    return new Promise(resolve => enqueue({ ...opts, resolve }));
}

export function ConfirmHost() {
    const [queue, setQueue] = React.useState([]);

    React.useEffect(() => {
        enqueue = (item) => setQueue(q => [...q, item]);
        return () => { enqueue = null; };
    }, []);

    const current = queue[0];
    // Keep the last request on screen while the dialog animates out.
    const lastShown = React.useRef(null);
    if (current) lastShown.current = current;
    const view = current || lastShown.current;

    const settle = (result) => {
        if (!current || current.settled) return;
        current.settled = true;
        current.resolve(result);
        setQueue(q => q.slice(1));
    };

    const danger = view?.tone === 'danger';
    const Icon = danger ? TriangleAlert : CircleAlert;

    return (
        <AlertDialog open={!!current} onOpenChange={(open) => { if (!open) settle(false); }}>
            <AlertDialogContent onEscapeKeyDown={() => settle(false)}>
                {view && (
                    <>
                        <AlertDialogHeader>
                            <span
                                aria-hidden="true"
                                className={cn(
                                    'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                                    danger ? 'bg-danger/10 text-danger' : 'bg-accent/10 text-accent'
                                )}
                            >
                                <Icon className="h-[18px] w-[18px]" />
                            </span>
                            <div className="min-w-0">
                                <AlertDialogTitle>{view.title || 'Are you sure?'}</AlertDialogTitle>
                                {view.description && (
                                    <AlertDialogDescription className="whitespace-pre-line">{view.description}</AlertDialogDescription>
                                )}
                            </div>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel onClick={() => settle(false)}>{view.cancelText || 'Cancel'}</AlertDialogCancel>
                            <AlertDialogAction variant={danger ? 'destructive' : 'primary'} onClick={() => settle(true)}>
                                {view.confirmText || 'Continue'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </>
                )}
            </AlertDialogContent>
        </AlertDialog>
    );
}
